import { VendorAssessmentStatus } from '@main/graphql/client-scalars';
import { Frequencies_Enum } from '@main/graphql/types.generated';
import dayjs, { Dayjs } from 'dayjs';

type FrequencyStatusDuration = {
  status: VendorAssessmentStatus;
  when: (date: Dayjs) => Dayjs;
};

const FREQUENCY_STATUS_DURATIONS: Record<Frequencies_Enum, FrequencyStatusDuration[]> = {
  [Frequencies_Enum.Daily]: [
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(1, 'day') },
  ],
  [Frequencies_Enum.Weekly]: [
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(1, 'week') },
  ],
  [Frequencies_Enum.Biweekly]: [
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(2, 'weeks') },
  ],
  [Frequencies_Enum.Monthly]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(3, 'weeks') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(1, 'month') },
  ],
  [Frequencies_Enum.Quarterly]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(2, 'months') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(3, 'months') },
  ],
  [Frequencies_Enum.Biannual]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(5, 'months') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(6, 'months') },
  ],
  [Frequencies_Enum.Annual]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(11, 'months') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(12, 'months') },
  ],
  [Frequencies_Enum.OneAndHalfYears]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(17, 'months') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(18, 'months') },
  ],
  [Frequencies_Enum.TwoYears]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(23, 'months') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(24, 'months') },
  ],
  [Frequencies_Enum.ThreeYears]: [
    { status: VendorAssessmentStatus.EXPIRING, when: (date) => date.add(35, 'months') },
    { status: VendorAssessmentStatus.OVERDUE, when: (date) => date.add(36, 'months') },
  ],
  [Frequencies_Enum.Indefinite]: [],
};

export function calculateStatus({
  frequency,
  lastReviewedDate,
}: {
  frequency: Frequencies_Enum;
  lastReviewedDate: string | undefined | null;
}) {
  const currentDate = dayjs();
  const durations = FREQUENCY_STATUS_DURATIONS[frequency];

  if (!lastReviewedDate) {
    return VendorAssessmentStatus.PENDING;
  }

  // Assume the status is "Reviewed" immediately after the last review date
  let status = VendorAssessmentStatus.REVIEWED;

  // If there are no specific durations (e.g., "Indefinite"), it remains "Reviewed"
  if (durations.length === 0) return status;

  for (const duration of durations) {
    const targetDate = duration.when(dayjs(lastReviewedDate));
    if (currentDate.isAfter(targetDate)) {
      status = duration.status; // Update status based on the condition
    }
  }

  return status;
}

export function getNextReviewDate({
  frequency,
  lastReviewedDate,
}: {
  frequency: Frequencies_Enum;
  lastReviewedDate: string | undefined | null;
}) {
  const currentDate = dayjs();
  const durations = FREQUENCY_STATUS_DURATIONS[frequency];

  for (const duration of durations) {
    const potentialNextDate = duration.when(dayjs(lastReviewedDate));
    if (currentDate.isBefore(potentialNextDate)) {
      // This is the next date when status should be updated
      return potentialNextDate.toDate();
    }
  }

  return null;
}

export function isVendorTerminated({ terminated_at }: { terminated_at?: string | null }) {
  return !!terminated_at;
}
